import { useState, useEffect, useRef, useMemo } from 'react';

/** Background colors for avatar circles in the dropdown */
export const AVATAR_BG_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-orange-500', 'bg-pink-500',
  'bg-violet-500', 'bg-cyan-500', 'bg-amber-500', 'bg-rose-500',
  'bg-teal-500', 'bg-indigo-500', 'bg-lime-500', 'bg-fuchsia-500',
];

export function getAvatarBg(installerId: number): string {
  return AVATAR_BG_COLORS[installerId % AVATAR_BG_COLORS.length];
}

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export const INSTALLER_PILL_COLORS = [
  { cls: 'bg-blue-100 text-blue-800',    family: 'blue' },
  { cls: 'bg-emerald-100 text-emerald-800', family: 'emerald' },
  { cls: 'bg-orange-100 text-orange-800', family: 'orange' },
  { cls: 'bg-pink-100 text-pink-800',    family: 'pink' },
  { cls: 'bg-violet-100 text-violet-800', family: 'violet' },
  { cls: 'bg-cyan-100 text-cyan-800',    family: 'cyan' },
  { cls: 'bg-amber-100 text-amber-800',  family: 'amber' },
  { cls: 'bg-rose-100 text-rose-800',    family: 'rose' },
  { cls: 'bg-teal-100 text-teal-800',    family: 'teal' },
  { cls: 'bg-indigo-100 text-indigo-800', family: 'indigo' },
  { cls: 'bg-lime-100 text-lime-800',    family: 'lime' },
  { cls: 'bg-fuchsia-100 text-fuchsia-800', family: 'fuchsia' },
];

function extractColorFamily(bgClass: string): string {
  const match = bgClass.match(/bg-(\w+)-/);
  return match ? match[1] : '';
}

export function getInstallerPillColor(installerId: number, cardBg: string): string {
  const avoid = extractColorFamily(cardBg);
  const idx = installerId % INSTALLER_PILL_COLORS.length;
  const pill = INSTALLER_PILL_COLORS[idx];
  if (pill.family !== avoid) return pill.cls;
  return INSTALLER_PILL_COLORS[(idx + 1) % INSTALLER_PILL_COLORS.length].cls;
}

interface InstallerDropdownProps {
  userMap: Map<number, string>;
  excludeIds: number[];
  onSelect: (id: number) => void;
  /** Show a "Geen" remove option at the top of the list */
  showRemoveOption?: boolean;
  /** Called when the user clicks the remove option */
  onRemove?: () => void;
}

export function InstallerDropdown({
  userMap,
  excludeIds,
  onSelect,
  showRemoveOption,
  onRemove,
}: InstallerDropdownProps) {
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return [...userMap.entries()]
      .filter(([id, name]) => id !== 0 && !excludeIds.includes(id) && (!term || name.toLowerCase().includes(term)))
      .sort((a, b) => a[1].localeCompare(b[1], 'nl'));
  }, [userMap, excludeIds, search]);

  return (
    <div className="absolute left-0 top-full mt-1 bg-td-navy rounded-xl shadow-xl z-30 w-56 py-1">
      <div className="px-2 py-1.5">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek installateur..."
          className="w-full px-2.5 py-1.5 text-sm bg-white/10 text-white placeholder-white/40 rounded-lg border border-white/20 focus:outline-none focus:border-white/40"
        />
      </div>
      {showRemoveOption && onRemove && (
        <button
          onClick={() => onRemove()}
          className="flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm text-white/70 hover:bg-white/10 transition-colors border-b border-white/10"
        >
          <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold text-white shrink-0">
            --
          </span>
          <span className="truncate">Geen</span>
        </button>
      )}
      <div className="max-h-52 overflow-y-auto">
        {filtered.length === 0 && (
          <p className="px-3 py-2 text-sm text-white/40">Geen resultaten</p>
        )}
        {filtered.map(([id, name]) => (
          <button
            key={id}
            onClick={() => onSelect(id)}
            className="flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm text-white/90 hover:bg-white/10 transition-colors"
          >
            <span className={`w-7 h-7 rounded-full ${getAvatarBg(id)} flex items-center justify-center text-[10px] font-bold text-white shrink-0`}>
              {getInitials(name)}
            </span>
            <span className="truncate">{name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
